import { ApiError } from '../features/identity/types';
import { apiErrorFromResponse } from './apiErrorFromResponse';
import { refreshAccessToken } from './tokenRefresh';
import { emitSubscriptionRestriction, restrictionFromProblem, restrictionMessage } from './subscriptionRestriction';
import { CLIENT_CONTEXT_HEADER, clientContextProblemFromCode, emitClientContextProblem, getClientContext } from './clientContext';

/** A request header that never leaves the browser: it marks a BACKGROUND lookup (a hint beside a form, an optional panel) whose subscription refusal the
 * caller handles itself, so the app-wide "This change is not available right now" notice is not raised for it. Plain-object `headers` only. */
export const QUIET_RESTRICTION_HEADER = 'X-Epn-Quiet-Restriction';

/**
 * The same request-wrapping behavior HttpIdentityGateway already established (credentials:'include' so
 * the httpOnly cookie Identity issued is sent to every module API, RFC 7807 problem-details error
 * unwrapping, 204-as-void) — factored out so each module's own Http*Gateway doesn't re-implement it.
 * A gateway calls `createJsonClient(baseUrl)` once and gets back the same `request<T>` shape.
 */
export function createJsonClient(baseUrl: string) {
  return async function request<T>(path: string, rawInit?: RequestInit, signal?: AbortSignal): Promise<T> {
    let init = rawInit;
    let quiet = false;
    const rawHeaders = rawInit?.headers;
    if (rawHeaders && typeof rawHeaders === 'object' && !Array.isArray(rawHeaders) && !(rawHeaders instanceof Headers) && QUIET_RESTRICTION_HEADER in rawHeaders) {
      const { [QUIET_RESTRICTION_HEADER]: _marker, ...rest } = rawHeaders as Record<string, string>;
      void _marker;
      quiet = true;
      init = { ...rawInit, headers: rest };
    }
    // Inside a client company (a partner user working for a client) every module request names the client this TAB is working in, so a request made after another tab changed the client is refused.
    const doFetch = () => {
      const clientContext = getClientContext();
      const contextHeader = clientContext ? { [CLIENT_CONTEXT_HEADER]: clientContext } : {};
      return fetch(`${baseUrl}${path}`, {
        ...init,
        ...(signal ? { signal } : {}),
        credentials: 'include',
        // A FormData body must NOT carry our JSON Content-Type: the browser sets multipart/form-data with its boundary.
        headers: typeof FormData !== 'undefined' && init?.body instanceof FormData ? { ...init?.headers, ...contextHeader } : { 'Content-Type': 'application/json', ...init?.headers, ...contextHeader },
      });
    };

    let response = await doFetch();

    // The access-token cookie is short-lived (15 min) — a 401 here is very often just that it expired
    // mid-session, not a real auth failure. Silently refresh once and retry before giving up; see
    // tokenRefresh.ts for why the in-flight refresh is deduplicated across callers.
    if (response.status === 401 && (await refreshAccessToken())) {
      response = await doFetch();
    }

    if (!response.ok) {
      // The shared helper parses the problem body once and reads X-Operation-Id (EPN-24C); it also keeps the
      // problem's stable `code` and parsed body, which the subscription-state handling below needs.
      const error = await apiErrorFromResponse(response, `Request to ${path} failed with ${response.status}`);

      // A module refused a partner's delegated session (another window changed the client, the access ended, or it could not be verified): tell the shell, which shows the right notice.
      const clientProblem = clientContextProblemFromCode(error.code);
      if (clientProblem) emitClientContextProblem(clientProblem);

      // EPN-105: a 403 that carries one of the subscription-state codes is not a permission problem and has no
      // human-readable `detail` — give it safe plain-language copy (so no screen shows a raw "failed with 403")
      // and tell the shell, which shows one global notice with the right next step.
      const restriction = restrictionFromProblem(response.status, error.details);
      if (restriction) {
        if (!quiet) emitSubscriptionRestriction(restriction);
        throw new ApiError(response.status, restrictionMessage(restriction), error.operationId, true, restriction.code, error.details);
      }

      throw error;
    }

    // Results.Ok() with no value sends status 200 with a genuinely empty body, not 204 — response.json()
    // throws "Unexpected end of JSON input" on that empty string, so an empty body is treated as "no
    // value" regardless of which 2xx status carried it.
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  };
}
